import { useEffect, useMemo, useState } from "react";
import { db } from "firebaseConfig";
import useFirestore from "hooks/useFirestore";
import { GenericConverter } from "utils/firestoreConverters";
import { buildWatermarkFreeUrlMap } from "utils/listings/watermarkFree";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

export type ListingWatermarkInfo = {
  loading: boolean;
  /** The marketplace listing doc (null when missing — e.g. webot-only listings). */
  listing: ProcessedListingData | null;
  imageUrls: string[];
  /**
   * original (watermarked) URL → clean URL. The clean versions are a SHARED cross-user cache, and
   * this map now always reflects it: there is no per-(user, listing) display gate any more.
   */
  cleanBySource: Map<string, string>;
  /** The realtor accepted the rights terms — the only thing that gates showing clean photos. */
  termsAccepted: boolean;
  cleanedCount: number;
  totalCount: number;
};

/**
 * Live watermark-removal state of a listing, straight from its marketplace doc
 * (`yad2-listings-latest`).
 *
 * Removal is a fact about the PROPERTY, not about a customer and not about who paid: once anyone
 * has cleaned a photo, the clean file exists and is world-readable, so every realtor who has
 * accepted the rights terms sees it. What used to be a per-user gate
 * (`watermarkRemovalRequestedBy`) protected nothing and produced a visible lie — see `cleanBySource`
 * and `User.watermarkTermsAcceptedAt`.
 */
export function useListingWatermarkInfo(listingId: string | undefined): ListingWatermarkInfo {
  const { userSettings } = useFirestore();
  const [listing, setListing] = useState<ProcessedListingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!listingId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    return db
      .collection("yad2-listings-latest")
      .doc(listingId)
      .withConverter(GenericConverter<ProcessedListingData>())
      .onSnapshot(
        (snapshot) => {
          setListing(snapshot.data() ?? null);
          setLoading(false);
        },
        (e) => {
          console.error("useListingWatermarkInfo - snapshot error", e);
          setLoading(false);
        }
      );
  }, [listingId]);

  const imageUrls = useMemo(() => listing?.imageUrls ?? [], [listing?.imageUrls]);
  /**
   * ALWAYS the real map (2026-09-30). It used to be emptied for a user who had not personally run
   * the removal, which made the panel report "0 מתוך 12 תמונות נקיות" on a listing the system
   * already held twelve clean copies of — and then offer to regenerate all twelve, for money, to
   * produce files that already existed.
   *
   * There is no per-listing display gate any more: `watermarkRemovalRequestedBy` protected images
   * that are `publicRead` with token-less URLs on a world-gettable collection, while being a public
   * roster of customer emails. The one remaining gate is the rights assertion,
   * `User.watermarkTermsAcceptedAt`, which is per REALTOR and forever.
   */
  const cleanBySource = useMemo(() => buildWatermarkFreeUrlMap(listing ?? {}), [listing]);
  const cleanedCount = imageUrls.filter((url) => cleanBySource.has(url)).length;

  return {
    loading,
    listing,
    imageUrls,
    cleanBySource,
    /** The realtor has accepted the rights terms — the only thing that gates showing clean photos. */
    termsAccepted: Boolean(userSettings?.watermarkTermsAcceptedAt),
    cleanedCount,
    totalCount: imageUrls.length
  };
}
